import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useIntl } from "react-intl";
import cuid from "cuid";
import moment from "moment";

import { PhoneIcon, EmailIcon } from "utilities/icons";
import { validateEmail } from "utilities";
import CountryCodeSelector from "components/CountryCodeSelector";
import TextInput from "components/TextInput";
import ButtonContained from "components/ButtonContained";

import { validateUser } from "actions/Auth";
import {
  setLoginPerformance,
  setErrorDetails
} from "actions/App";

const InitialSignIn = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const countryProp = useSelector((state) => state.app.country);
  const loginPerformance = useSelector((state) => state.app.loginPerformance);
  const errorDetails = useSelector((state) => state.app.errorDetails);

  const sessionConfirm = JSON.parse(sessionStorage.getItem("login_confirm"));
  const localConfirm = JSON.parse(localStorage.getItem("login_confirm"));
  const nseLoginData = JSON.parse(localStorage.getItem("nse_login_obj"));
  const parsedData = sessionConfirm || localConfirm || nseLoginData || {};

  const [country, setCountry] = useState(countryProp);
  const [phone, setPhone] = useState(parsedData.phone || "");
  const [email, setEmail] = useState(parsedData.email || "");
  const [phoneLogin, setPhoneLogin] = useState(
    parsedData.phone ? true : false
  );
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    document.title = "Login | Smart Entry";
  }, []);

  useEffect(() => {
    return () => {
      // Runs on unmount
      dispatch(setErrorDetails(null));
    };
  }, []);

  useEffect(() => {
    setCountry(countryProp);
  }, [countryProp]);

  useEffect(() => {
    if (errorDetails) {
      setHasError(true);
      setErrorMessage(errorDetails.description);
    } else {
      setHasError(false);
      setErrorMessage("");
    }
  }, [errorDetails]);

  const validate = (e) => {
    e.preventDefault();

    if (!phoneLogin && !validateEmail(email)) {
      setHasError(true);
      setErrorMessage(intl.formatMessage({id: "notifications.error.incorrect.email"}));
      return;
    }
    const deviceUUID = cuid();
    sessionStorage.setItem(
      "initial_phone_login",
      JSON.stringify(phoneLogin ? "yes" : "no")
    );
    dispatch(
      setLoginPerformance({
        ...loginPerformance,
        start_time: moment(),
        loginType: phoneLogin ? "Phone" : "Email",
        emailOrPhone: phoneLogin ? phone : email,
      })
    );
    dispatch(
      validateUser({
        companyUUID: "-1",
        countryCode: phoneLogin ? country?.phone[0].toString() : "",
        countryName: phoneLogin ? country?.name : "",
        phone: phoneLogin ? phone : "",
        email: phoneLogin ? "" : email,
        deviceUUID,
      })
    );
  };

  const clearError = () => {
    setHasError(false);
    setErrorMessage("");
    dispatch(setErrorDetails(null));
  }

  return (
    <div className="h-full flex flex-col">
      <label className="flex justify-between items-center h-10">
        <h5 className="tw-heading-5 font-bold text-black">
          {intl.formatMessage({ id: "form.signIn" })}
        </h5>
        <div className="flex space-x-2">
          <div
            id="Initial-Login-Phone-Select"
            data-cy="Initial-Login-Phone-Select"
            onClick={() => setPhoneLogin(true)}
            className={`p-2 rounded cursor-pointer hover:opacity-80 ${
              phoneLogin ? "bg-[var(--color-blue-600)]" : "bg-[var(--color-gray-400)]"
            }`}
          >
            <PhoneIcon color="white" />
          </div>
          <div
            id="Initial-Login-Email-Select"
            data-cy="Initial-Login-Email-Select"
            onClick={() => setPhoneLogin(false)}
            className={`p-2 rounded cursor-pointer hover:opacity-80 ${
              !phoneLogin ? "bg-[var(--color-blue-600)]" : "bg-[var(--color-gray-400)]"
            }`}
          >
            <EmailIcon color="white" />
          </div>
        </div>
      </label>

      <div className="flex flex-col justify-between">
        <form>
          {phoneLogin ? (
            <div className="mt-6 mb-16 flex items-center gap-2 h-[53px]">
              <CountryCodeSelector
                id="Initial-Login-Country-Code-Selector"
                dataCy="Initial-Login-Country-Code-Selector"
                selectedCountry={country}
                onSelect={(country) => setCountry(country)}
              />
              <TextInput
                id="Initial-Login-Phone-Text-Input"
                dataCy="Initial-Login-Phone-Text-Input"
                type="tel"
                name="tel"
                placeholder={`${intl.formatMessage({id: "form.phone"})}`}
                onChange={(e) => {
                  e.preventDefault();
                  setPhone(e.target.value)
                }}
                value={phone}
                validateInput={(val) => /^\d*$/.test(val)} // only digits
              />
            </div>
          ) : (
            <div className="mt-6 mb-16 w-full">
              <TextInput
                id="Initial-Login-Email-Text-Input"
                dataCy="Initial-Login-Email-Text-Input"
                type="email"
                name="email"
                placeholder={intl.formatMessage({ id: `form.email` })}
                onChange={(e) => {
                  if (hasError) {
                    clearError();
                  }
                  setEmail(e.target.value);
                }}
                value={email}
                hasError={hasError ? true : false}
                errorMessage={hasError ? errorMessage : null}
              />
            </div>
          )}
          <div className="flex justify-end">
            <ButtonContained
              id="Initial-Login-Continue-Button"
              dataCy="Initial-Login-Continue-Button"
              text={intl.formatMessage({id: "form.continue"})}
              type="default"
              buttonType="submit"
              onClick={(e) => validate(e)}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default InitialSignIn;